<script setup>
import { useAxios } from '@/hooks/useAxios';
import { computed, onMounted, ref, watch } from 'vue';

// Props 정의
const props = defineProps({
    modelValue: {
        type: Object,
        default: () => []
    },
    inputId: {
        type: String,
        default: ''
    },
    keyVal: {
        type: String,
        default: ''
    },
    options: {
        type: Array,
        default: () => []
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'select', 'update-options']);
const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const deliId = ref(props.keyVal);
const errMessage = ref(props.errmsg);
const ValidationMsg = ref('');
const selectedCity = ref('');
const selectedCounty = ref('');
const selectedDistricts = ref([]);
const selectedItems = ref([]);
const structuredData = ref({});
const disabledItems = ref([]);

onMounted(() => {
    //console.log(deliId.value);
    //setCity();
});

const setCity = () => {
    try {
        let url = deliId.value ? `/api/delivery/area/${deliId.value}` : '/api/delivery/area';
        //console.log(url);
        const config = {
            method: 'get'
        };
        const { data, error, execute } = useAxios(url, config, {
            immediate: false,
            onSuccess(response) {
                structuredData.value = processData(response.data.data);
                initializeSelectedItems(response.data.data);
            },
            onError(err) {
                console.error('Error:', err.message);
            }
        });

        execute();
    } catch (error) {
        console.error('구역 조회 오류 발생:', error);
    }
};

const initializeSelectedItems = (data) => {
    selectedItems.value = data.filter((item) => item.assign_state === 'DONE_ALLOW').map((item) => formatToOutput(item));
    disabledItems.value = data.filter((item) => item.assign_state === 'DONE_DENY');
    updateSelections();
    emitUpdate(selectedItems.value, 'select');
};

const formatToOutput = (item) => ({
    // delivery_id: deliId.value || '',
    city: item.city,
    county: item.county,
    district: item.district
});

const processData = (data) => {
    const result = {};
    data.forEach((item) => {
        if (!result[item.city]) result[item.city] = {};
        if (!result[item.city][item.county]) result[item.city][item.county] = [];
        if (!result[item.city][item.county].includes(item.district)) {
            result[item.city][item.county].push(item.district);
        }
    });
    return result;
};

const cities = computed(() => Object.keys(structuredData.value));
const counties = computed(() => (selectedCity.value ? Object.keys(structuredData.value[selectedCity.value]) : []));
const districts = computed(() => {
    if (selectedCity.value && selectedCounty.value) {
        return ['전체', ...structuredData.value[selectedCity.value][selectedCounty.value]];
    }
    return [];
});

// 수정된 formatItems 함수
const formatItems = (items, columns) => {
    const result = [];
    for (let i = 0; i < items.length; i += columns) {
        result.push(items.slice(i, i + columns));
    }
    return result;
};

// 수정된 computed 속성
const formattedCities = computed(() => formatItems(cities.value, 2));
const formattedCounties = computed(() => formatItems(counties.value, 3));
const formattedDistricts = computed(() => formatItems(districts.value, 4));

const selectCity = (city) => {
    selectedCity.value = city;
    selectedCounty.value = '';
    selectedDistricts.value = [];
    updateSelections();
};

const selectCounty = (county) => {
    selectedCounty.value = county;
    selectedDistricts.value = [];
    updateSelections();
};

const updateSelections = () => {
    if (selectedCity.value) {
        // 선택된 city에 대한 county 목록 업데이트
        const countiesForCity = [...new Set(selectedItems.value.filter((item) => item.city === selectedCity.value).map((item) => item.county))];

        if (countiesForCity.length > 0 && !selectedCounty.value) {
            selectedCounty.value = countiesForCity[0];
        }

        if (selectedCounty.value) {
            // 선택된 city와 county에 대한 district 목록 업데이트
            const selectedDistrictsForCurrentCounty = selectedItems.value.filter((item) => item.city === selectedCity.value && item.county === selectedCounty.value).map((item) => item.district);

            selectedDistricts.value = selectedDistrictsForCurrentCounty;

            // '전체' 항목 처리
            if (districts.value && selectedDistrictsForCurrentCounty.length === districts.value.length - 1) {
                selectedDistricts.value.push('전체');
            }
        }
    }
};

const selectDistrict = (district) => {
    if (isDistrictDisabled(district)) return;

    if (district === '전체') {
        if (selectedDistricts.value.includes('전체')) {
            // 전체 선택 해제
            selectedDistricts.value = [];
            selectedItems.value = selectedItems.value.filter((item) => item.city !== selectedCity.value || item.county !== selectedCounty.value);
        } else {
            // 전체 선택 (disabled 항목 제외)
            const availableDistricts = districts.value.slice(1).filter((d) => !isDistrictDisabled(d));
            selectedDistricts.value = ['전체', ...availableDistricts];
            availableDistricts.forEach((d) => {
                if (!selectedItems.value.some((item) => item.city === selectedCity.value && item.county === selectedCounty.value && item.district === d)) {
                    selectedItems.value.push({
                        delivery_id: deliId.value,
                        city: selectedCity.value,
                        county: selectedCounty.value,
                        district: d
                    });
                }
            });
        }
    } else {
        const index = selectedDistricts.value.indexOf(district);
        if (index === -1) {
            selectedDistricts.value.push(district);
            selectedItems.value.push({
                delivery_id: deliId.value,
                city: selectedCity.value,
                county: selectedCounty.value,
                district: district
            });
        } else {
            selectedDistricts.value.splice(index, 1);
            const itemIndex = selectedItems.value.findIndex((item) => item.city === selectedCity.value && item.county === selectedCounty.value && item.district === district);
            if (itemIndex !== -1) {
                selectedItems.value.splice(itemIndex, 1);
            }
        }
        // '전체' 항목 처리
        const availableDistricts = districts.value.slice(1).filter((d) => !isDistrictDisabled(d));
        if (selectedDistricts.value.length === availableDistricts.length) {
            if (!selectedDistricts.value.includes('전체')) {
                selectedDistricts.value.push('전체');
            }
        } else {
            const allIndex = selectedDistricts.value.indexOf('전체');
            if (allIndex !== -1) {
                selectedDistricts.value.splice(allIndex, 1);
            }
        }
    }
    emitUpdate(selectedItems.value, 'select');
};

const isDistrictDisabled = (district) => {
    return disabledItems.value.some((item) => item.city === selectedCity.value && item.county === selectedCounty.value && item.district === district);
};

const isDistrictSelected = (district) => {
    return selectedDistricts.value.includes(district) || selectedItems.value.some((item) => item.city === selectedCity.value && item.county === selectedCounty.value && item.district === district && item.assign_state === 'DONE_ALLOW');
};

const removeSelectedItem = (index) => {
    selectedItems.value.splice(index, 1);
    updateSelections();
    emitUpdate(selectedItems.value, 'select');
};

const removeAll = () => {
    if (selectedDistricts.value.length === 0 && selectedItems.value.length === 0) return;
    selectedDistricts.value = [];
    selectedItems.value = [];
    emitUpdate(selectedItems.value, 'select');
};

const emitUpdate = (value, event) => {
    inputValue.value = value;
    emit(
        'update:modelValue',
        value.map((item) => formatToOutput(item))
    );
    emit('input', {
        id: props.inputId,
        value: value
    });
};

watch(
    () => props.options,
    (newOptions) => {
        structuredData.value = processData(newOptions);
        initializeSelectedItems(newOptions);
    },
    { immediate: true }
);

watch(
    () => props.modelValue,
    (newVal) => {
        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

watch(
    () => props.keyVal,
    (newVal) => {
        deliId.value = newVal.toString() || '';
    },
    { immediate: true, deep: true }
);
</script>

<template>
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>

        <div class="list-boxes-container">
            <!-- City List Box -->
            <div class="button-grid-container city-box">
                <div class="button-grid">
                    <div v-for="(row, rowIndex) in formattedCities" :key="rowIndex" class="button-row">
                        <button v-for="city in row" :key="city" @click="selectCity(city)" class="grid-button" :class="{ selected: selectedCity === city }">
                            {{ city }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- County List Box -->
            <div class="button-grid-container county-box">
                <div class="button-grid">
                    <div v-for="(row, rowIndex) in formattedCounties" :key="rowIndex" class="button-row">
                        <button v-for="county in row" :key="county" @click="selectCounty(county)" class="grid-button" :class="{ selected: selectedCounty === county }">
                            {{ county }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- District List Box -->
            <div class="button-grid-container district-box">
                <div class="button-grid">
                    <div v-for="(row, rowIndex) in formattedDistricts" :key="rowIndex" class="button-row">
                        <button
                            v-for="district in row"
                            :key="district"
                            @click="selectDistrict(district)"
                            class="grid-button"
                            :class="{ selected: isDistrictSelected(district), disabled: isDistrictDisabled(district) }"
                            :disabled="isDistrictDisabled(district)"
                        >
                            {{ district }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="select-count"><button @click="removeAll()" class="removeall-button">전체취소</button> {{ selectedItems.length }} 건</div>
        <div class="selected-items-container">
            <div v-for="(item, index) in selectedItems" :key="index" class="selected-item">
                <span class="item-text">{{ `${item.city} > ${item.county} > ${item.district}` }}</span>
                <button @click="removeSelectedItem(index)" class="remove-button">X</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171;
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.list-boxes-container {
    display: flex;
    width: 100%;
    gap: 1rem;
    margin-bottom: 1rem;
}

.button-grid-container {
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    padding: 1rem;
    height: 14rem;
    /* max-height: 300px; */
    overflow-y: auto;
}

.city-box {
    flex: 2.5;
}

.county-box {
    flex: 2.5;
}

.district-box {
    flex: 4;
}

.button-grid {
    display: grid;
    gap: 0.5rem;
}

.button-row {
    display: flex;
    gap: 0.5rem;
}

.grid-button {
    flex: 1;
    padding: 0.3rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    color: #374151;
    background-color: white;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.3rem;
    cursor: pointer;
    transition:
        background-color 0.2s,
        box-shadow 0.2s;
    min-height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.grid-button:hover {
    background-color: #eef0f1;
}

.grid-button:focus {
    outline: none;
}

.grid-button.selected {
    box-shadow: 0 0 0 0.1rem #0038ea;
    opacity: 0.9;
}

.selected-items-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.2rem;
    margin-top: 0.1rem;
    height: 11.2rem;
    overflow-y: auto;
    padding: 0.5rem 0.4rem;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    justify-content: flex-start;
    align-content: flex-start;
    width: 100%;
}

.selected-item {
    background-color: #f1f3f4;
    padding: 0.6rem;
    border-radius: 0.3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    height: 1.7rem;
    margin-right: 0.2rem;
    margin-bottom: 0.2rem;
    width: calc(33.33% - 0.35rem);
    box-sizing: border-box;
}

.item-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-grow: 1;
    margin-left: 0.3rem;
}

.remove-button {
    margin-left: 0.5rem;
    color: red;
    width: 0.5rem;
    font-family: 'SCoreDream400';
    font-size: 0.75rem;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background: none;
    border: none;
    margin-right: 0.2rem;
}

.remove-button:hover {
    font-family: 'SCoreDream600';
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.grid-button.disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.select-count {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    opacity: 0.7;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 0.35rem;
    margin-right: 0.25rem;
    height: 0.5rem;
}

.removeall-button {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    opacity: 0.8;
    cursor: pointer;
    margin-right: 1rem;
}

.removeall-button:hover {
    opacity: 1;
    cursor: pointer;
}
</style>
